import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useDeferredValue,
  createContext,
  useContext,
} from "react";
import {
  useParams,
  useSearchParams,
  useNavigate,
  useBlocker,
} from "react-router-dom";
import { Puck, type Config, type Data } from "@puckeditor/core";
import {
  ContactSectionRuntimeProvider,
  LanguageSwitcherRuntimeProvider,
  puckConfig,
  type ContactRuntimeResolvedForm,
  type ContactRuntimeSubmitPayload,
} from "@staylayer/puck-components";
import { usePage } from "../hooks/usePage";
import { useSavePage } from "../hooks/useSavePage";
import { usePublishPage } from "../hooks/usePublishPage";
import { useVersions } from "../hooks/useVersions";
import { useSettings } from "../hooks/useSettings";
import { canPublishContent } from "../auth/access";
import { useAuth } from "../auth/useAuth";
import client from "../api/client";
import { getFormStudio } from "../api/forms";
import { restoreVersion, updatePage } from "../api/pages";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { formatDate } from "../lib/formatDate";
import {
  ArrowLeft,
  Save,
  Globe,
  Eye,
  History,
  Monitor,
  Loader2,
  RotateCcw,
  X,
  Search,
  EyeOff,
  AlertCircle,
  Image,
  Link,
} from "lucide-react";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import "@puckeditor/core/puck.css";
import {
  useEditorMentor,
  useOnboardingMentor,
  RestartMentorButton,
} from "../onboarding";

const AUTOSAVE_INTERVAL = 3_000;
const DEFAULT_BRAND_LOGO_URL = "/images/logo.png";
const BRAND_COMPONENT_TYPES = ["Navbar", "Footer"] as const;
const ComponentSearchContext = createContext("");

function normalizeComponentSearchValue(value: string) {
  return value.trim().toLowerCase();
}

function formatComponentSearchLabel(value: string) {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .trim();
}

function buildComponentSearchIndex(config: Config) {
  const index: Record<string, string> = {};

  for (const category of Object.values(config.categories || {})) {
    const categoryTitle = category?.title || "";
    const categoryComponents = Array.isArray(category?.components)
      ? category.components
      : [];

    for (const componentName of categoryComponents) {
      const componentLabel = formatComponentSearchLabel(componentName);
      index[componentName] = normalizeComponentSearchValue(
        `${componentLabel} ${categoryTitle}`,
      );
    }
  }

  return index;
}

function matchesComponentSearch(
  indexValue: string | undefined,
  searchValue: string,
) {
  const normalizedSearch = normalizeComponentSearchValue(searchValue);

  if (!normalizedSearch) {
    return true;
  }

  return (indexValue || "").includes(normalizedSearch);
}

function ComponentSearchDrawer({
  children,
  componentSearchIndex,
}: {
  children: React.ReactNode;
  componentSearchIndex: Record<string, string>;
}) {
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const hasComponentSearchMatches = useMemo(() => {
    const normalizedSearch = normalizeComponentSearchValue(deferredSearch);

    if (!normalizedSearch) {
      return true;
    }

    return Object.values(componentSearchIndex).some((indexValue) =>
      indexValue.includes(normalizedSearch),
    );
  }, [componentSearchIndex, deferredSearch]);

  useEffect(() => {
    const root = resultsRef.current;

    if (!root) {
      return;
    }

    const normalizedSearch = normalizeComponentSearchValue(deferredSearch);
    const categoryBlocks = Array.from(root.children).filter(
      (child): child is HTMLDivElement =>
        child instanceof HTMLDivElement &&
        child.className.includes("_ComponentList_"),
    );

    for (const categoryBlock of categoryBlocks) {
      if (!normalizedSearch) {
        categoryBlock.hidden = false;
        continue;
      }

      const hasVisibleItems = Boolean(
        categoryBlock.querySelector(
          "[data-component-search-item]:not([hidden])",
        ),
      );

      categoryBlock.hidden = !hasVisibleItems;
    }
  }, [deferredSearch]);

  return (
    <ComponentSearchContext.Provider value={deferredSearch}>
      <div className="flex h-full flex-col bg-white">
        <div className="shrink-0 border-b border-slate-200 bg-white/95 px-3 pb-3 pt-2 backdrop-blur">
          <label className="block text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            Search components
          </label>
          <div className="relative mt-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Find hero, FAQ, gallery..."
              className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-10 text-sm text-slate-900 outline-none transition focus:border-slate-300 focus:bg-white focus:ring-2 focus:ring-slate-200"
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear component search"
                className="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Search by block name or section.
          </p>
        </div>
        <div ref={resultsRef} className="min-h-0 flex-1">
          {children}
          {!hasComponentSearchMatches ? (
            <div className="px-4 py-5 text-sm text-slate-500">
              No components match “{search.trim()}”.
            </div>
          ) : null}
        </div>
      </div>
    </ComponentSearchContext.Provider>
  );
}

function ComponentSearchDrawerItem({
  children,
  name,
  componentSearchIndex,
}: {
  children: React.ReactNode;
  name: string;
  componentSearchIndex: Record<string, string>;
}) {
  const searchValue = useContext(ComponentSearchContext);
  const hidden = !matchesComponentSearch(
    componentSearchIndex[name],
    searchValue,
  );

  return (
    <div
      data-component-search-item
      data-onboarding-target={`palette:item:${name}`}
      hidden={hidden || undefined}
    >
      {children}
    </div>
  );
}

function supportsInlineMarkup(field: {
  type?: string;
  contentEditable?: boolean;
}) {
  return Boolean(
    field?.contentEditable &&
    (field?.type === "text" || field?.type === "textarea"),
  );
}

function MarkupFieldChrome({
  children,
  field,
  readOnly,
}: {
  children: React.ReactNode;
  field: { type?: string; contentEditable?: boolean };
  readOnly?: boolean;
}) {
  if (!supportsInlineMarkup(field) || readOnly) {
    return <>{children}</>;
  }

  return (
    <div className="space-y-2.5">
      <div className="rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.94))] px-3 py-3 shadow-[0_10px_30px_-24px_rgba(15,23,42,0.45)]">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            Formatting
          </span>
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700">
            Bold with **text**
          </span>
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700">
            Color with [text]{"{#0ea5e9}"}
          </span>
        </div>
        <p className="mt-2 text-xs leading-5 text-slate-600">
          Example: [**StayLayer**]{"{#0ea5e9}"}
        </p>
      </div>
      {children}
    </div>
  );
}

function getDraftKey(slug: string, locale: string) {
  return `draft__${slug}__${locale}`;
}

function withSiteBrandingDefaults(
  config: Config,
  settings?: { logoUrl?: string; siteName?: string } | null,
) {
  const logoUrl = settings?.logoUrl?.trim();

  if (!logoUrl) {
    return config;
  }

  const siteName = settings?.siteName?.trim() || "Site";
  const components = { ...config.components };

  for (const componentType of BRAND_COMPONENT_TYPES) {
    const componentConfig = components[componentType];
    if (!componentConfig) {
      continue;
    }

    const defaultProps = componentConfig.defaultProps || {};
    const currentLogoUrl = String(defaultProps.logoImageUrl || "").trim();
    const logoType = String(defaultProps.logoType || "").trim();
    const shouldUseSiteLogo =
      (!currentLogoUrl || currentLogoUrl === DEFAULT_BRAND_LOGO_URL) &&
      (!logoType || logoType === "image" || logoType === "both");

    if (!shouldUseSiteLogo) {
      continue;
    }

    components[componentType] = {
      ...componentConfig,
      defaultProps: {
        ...defaultProps,
        logoImageUrl: logoUrl,
        logoImageAlt:
          defaultProps.logoImageAlt && defaultProps.logoImageAlt !== "Logo"
            ? defaultProps.logoImageAlt
            : `${siteName} logo`,
        logoText:
          defaultProps.logoText && defaultProps.logoText !== "Your Brand"
            ? defaultProps.logoText
            : siteName,
        logoType: defaultProps.logoType || "image",
      },
    };
  }

  return {
    ...config,
    components,
  };
}

export default function EditorPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const locale = searchParams.get("locale") || "en";
  const navigate = useNavigate();
  const { session } = useAuth();
  const activeSiteId = session?.activeSite?.id ?? null;
  const queryClient = useQueryClient();

  const { data: page, isLoading, error } = usePage(slug, locale);
  const { data: settings } = useSettings();
  const saveMutation = useSavePage();
  const publishMutation = usePublishPage();
  const formStudioQuery = useQuery({
    queryKey: ["form-studio", activeSiteId],
    queryFn: () => getFormStudio(activeSiteId!),
    enabled: Boolean(activeSiteId),
    staleTime: 60_000,
  });

  const [isDirty, setIsDirty] = useState(false);
  const [historyOpenedCount, setHistoryOpenedCount] = useState(0);
  const [previewOpenedCount, setPreviewOpenedCount] = useState(0);
  const [showVersions, setShowVersions] = useState(false);
  const [showSeoPanel, setShowSeoPanel] = useState(false);
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const [draftTime, setDraftTime] = useState<string | null>(null);
  const [showDesktopWarning, setShowDesktopWarning] = useState(false);
  const [restoreConfirm, setRestoreConfirm] = useState<string | null>(null);
  const [puckKey, setPuckKey] = useState(0);
  const latestDataRef = useRef<Data | null>(null);
  // Observed (throttled) Puck data for the onboarding mentor. Updated
  // from onChange via a 300ms trailing-edge throttle so validators don't
  // re-run on every keystroke.
  const [observedData, setObservedData] = useState<Data | null>(null);
  const observedThrottleRef = useRef<{
    timer: number | null;
    pending: Data | null;
  }>({ timer: null, pending: null });

  // ─── Page-level SEO state ─────────────────────────────────
  const [seoForm, setSeoForm] = useState({
    seoTitle: "",
    seoDescription: "",
    targetKeywords: "",
    internalBrief: "",
    seoOgImage: "",
    seoCanonical: "",
    seoNoindex: false,
  });
  const [seoDirty, setSeoDirty] = useState(false);

  const { data: versionsData } = useVersions(slug, locale, showVersions);

  const saveSeoMutation = useMutation({
    mutationFn: (form: typeof seoForm) => updatePage(slug!, locale, form),
    onSuccess: () => {
      toast.success(
        page?.published
          ? "SEO settings saved. Published changes update automatically."
          : "SEO settings saved to draft.",
      );
      setSeoDirty(false);
      void queryClient.invalidateQueries({ queryKey: ["page", slug, locale] });
    },
    onError: () => {
      toast.error("Failed to save SEO settings");
    },
  });

  const canPublish = canPublishContent(session);
  const onboardingMentor = useOnboardingMentor();

  useEffect(() => {
    setHistoryOpenedCount(0);
    setPreviewOpenedCount(0);
  }, [onboardingMentor.session?.sessionId]);

  const resolveContactForm = useCallback(
    async ({
      formKey,
      pageSlug,
      locale: requestedLocale,
    }: {
      formKey?: string;
      pageSlug?: string | null;
      locale?: string | null;
    }) => {
      if (!activeSiteId) {
        return null;
      }

      const { data } = await client.get<ContactRuntimeResolvedForm | null>(
        "/public/forms/resolve",
        {
          params: {
            siteId: activeSiteId,
            locale: requestedLocale || locale,
            ...(pageSlug ? { pageSlug } : {}),
            ...(formKey ? { formKey } : {}),
          },
        },
      );

      return data;
    },
    [activeSiteId, locale],
  );

  const submitContactPreview = useCallback(
    async (payload: ContactRuntimeSubmitPayload) => {
      if (!activeSiteId) {
        throw new Error("No active site is selected for this preview.");
      }

      await client.post("/public/submissions", {
        siteId: activeSiteId,
        formType: payload.formType || "CONTACT",
        ...payload,
      });
    },
    [activeSiteId],
  );

  const availableForms = (formStudioQuery.data?.definitions ?? [])
    .map((definition) => ({
      id: definition.id,
      key: definition.key,
      name: definition.name,
      status: definition.status,
      assignment: definition.assignment,
    }))
    .sort((left, right) => {
      if (left.status === right.status) {
        return left.name.localeCompare(right.name);
      }

      if (left.status === "ACTIVE") {
        return -1;
      }

      if (right.status === "ACTIVE") {
        return 1;
      }

      return left.name.localeCompare(right.name);
    });

  const brandedPuckConfig = useMemo(
    () => withSiteBrandingDefaults(puckConfig, settings),
    [settings?.logoUrl, settings?.siteName],
  );
  const componentSearchIndex = useMemo(
    () => buildComponentSearchIndex(brandedPuckConfig),
    [brandedPuckConfig],
  );

  const contactSectionRuntime = {
    pageSlug: slug ?? null,
    locale,
    availableForms,
    loadingForms: formStudioQuery.isLoading,
    formsError: formStudioQuery.error
      ? formStudioQuery.error instanceof Error
        ? formStudioQuery.error.message
        : "Failed to load Form Studio definitions."
      : null,
    resolveForm: activeSiteId ? resolveContactForm : undefined,
    submitForm: activeSiteId ? submitContactPreview : undefined,
    notify: ({
      type,
      message,
    }: {
      type: "success" | "error";
      message: string;
    }) => {
      if (type === "success") {
        toast.success(message);
        return;
      }

      toast.error(message);
    },
  };
  const languageSwitcherRuntime = {
    pageSlug: slug ?? null,
    currentLocale: locale,
    defaultLocale: settings?.defaultLocale || "en",
    availableLocales: Array.from(
      new Set([...(settings?.activeLocales ?? []), locale].filter(Boolean)),
    ),
    buildHref: (nextLocale: string) =>
      slug ? `/preview/${slug}?locale=${nextLocale}` : "#",
  };

  // Check for local draft on mount
  useEffect(() => {
    if (!slug || !page) return;
    const key = getDraftKey(slug, locale);
    const stored = localStorage.getItem(key);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (
          parsed.savedAt &&
          new Date(parsed.savedAt) > new Date(page.updatedAt)
        ) {
          setShowDraftBanner(true);
          setDraftTime(parsed.savedAt);
        }
      } catch {
        localStorage.removeItem(key);
      }
    }
  }, [slug, locale, page]);

  // Populate SEO form from loaded page
  useEffect(() => {
    if (page) {
      setSeoForm({
        seoTitle: page.seoTitle ?? "",
        seoDescription: page.seoDescription ?? "",
        targetKeywords: page.targetKeywords ?? "",
        internalBrief: page.internalBrief ?? "",
        seoOgImage: page.seoOgImage ?? "",
        seoCanonical: page.seoCanonical ?? "",
        seoNoindex: page.seoNoindex ?? false,
      });
      setSeoDirty(false);
    }
  }, [page]);

  // Desktop-only warning
  useEffect(() => {
    const check = () => setShowDesktopWarning(window.innerWidth < 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Auto-save to localStorage
  useEffect(() => {
    if (!slug) return;
    const timer = setInterval(() => {
      if (latestDataRef.current && isDirty) {
        const key = getDraftKey(slug, locale);
        localStorage.setItem(
          key,
          JSON.stringify({
            data: latestDataRef.current,
            savedAt: new Date().toISOString(),
          }),
        );
      }
    }, AUTOSAVE_INTERVAL);
    return () => clearInterval(timer);
  }, [slug, locale, isDirty]);

  // Warn on navigation away
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  useBlocker(
    useCallback(() => {
      if (isDirty) {
        return !window.confirm("You have unsaved changes. Leave anyway?");
      }
      return false;
    }, [isDirty]),
  );

  const handleSave = useCallback(
    (data: Data) => {
      if (!slug) return;
      saveMutation.mutate(
        { slug, locale, puckData: data as unknown as Record<string, unknown> },
        {
          onSuccess: () => {
            setIsDirty(false);
            localStorage.removeItem(getDraftKey(slug, locale));
            toast.success(
              page?.published
                ? "Page saved. Published changes update automatically."
                : "Draft saved. Publish when ready.",
            );
          },
          onError: () => {
            toast.error("Save failed — please retry");
          },
        },
      );
    },
    [slug, locale, saveMutation],
  );

  const handlePublish = () => {
    if (!slug) return;
    publishMutation.mutate(
      { slug, locale, publish: !page?.published },
      {
        onSuccess: () => {
          toast.success(
            page?.published ? "Page unpublished" : "Page published",
          );
        },
        onError: () => {
          toast.error("Failed to update publish status");
        },
      },
    );
  };

  const handlePreviewOpen = useCallback(() => {
    if (!slug) return null;
    setPreviewOpenedCount((count) => count + 1);
    return window.open(`/preview/${slug}?locale=${locale}`, "_blank");
  }, [slug, locale]);

  const handleHistoryToggle = useCallback(() => {
    const nextShowVersions = !showVersions;
    setShowVersions(nextShowVersions);
    setShowSeoPanel(false);
    if (nextShowVersions) {
      setHistoryOpenedCount((count) => count + 1);
    }
  }, [showVersions]);

  const restoreMutation = useMutation({
    mutationFn: (versionId: string) => restoreVersion(slug!, versionId, locale),
    onSuccess: async () => {
      toast.success("Version restored");
      await queryClient.refetchQueries({ queryKey: ["page", slug, locale] });
      queryClient.invalidateQueries({ queryKey: ["versions", slug, locale] });
      setRestoreConfirm(null);
      setShowVersions(false);
      setIsDirty(false);
      setPuckKey((prev) => prev + 1);
    },
    onError: () => {
      toast.error("Failed to restore version");
    },
  });

  const handleRestoreDraft = () => {
    if (!slug) return;
    const key = getDraftKey(slug, locale);
    const stored = localStorage.getItem(key);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        latestDataRef.current = parsed.data;
        setShowDraftBanner(false);
        // Force remount by incrementing the key
        setPuckKey((prev) => prev + 1);
        // Update page data for the new mount
        queryClient.setQueryData(["page", slug, locale], (old: unknown) => ({
          ...(old as Record<string, unknown>),
          puckData: parsed.data,
        }));
      } catch {
        localStorage.removeItem(key);
      }
    }
  };

  const handleDismissDraft = () => {
    if (!slug) return;
    localStorage.removeItem(getDraftKey(slug, locale));
    setShowDraftBanner(false);
  };

  // Seed the throttled observed-data state from the loaded page so the
  // mentor's auto-pass predicates see the initial content.
  useEffect(() => {
    if (page?.puckData) {
      setObservedData(page.puckData as Data);
    }
  }, [page?.puckData]);

  // ─── Onboarding mentor (Build With Me) ───────────────────────────────────
  // Called unconditionally so hook order stays stable across renders.
  // When the feature flag is off, useEditorMentor returns `{ overlay: null }`.
  const mentor = useEditorMentor({
    data: observedData,
    pageTitle: page?.title ?? null,
    pagePublished: page?.published ?? false,
    isDirty,
    historyOpenedCount,
    previewOpenedCount,
    canPublish,
    showSeoPanel,
    showVersions,
    setShowSeoPanel,
    setShowVersions,
    invokeSave: () => {
      if (latestDataRef.current) {
        handleSave(latestDataRef.current);
      }
    },
    invokePreview: handlePreviewOpen,
    invokePublish: handlePublish,
    navigateToNewPage: () => navigate("/pages/new"),
    publishError: publishMutation.isError,
  });

  if (showDesktopWarning) {
    return (
      <div className="flex min-h-100 items-center justify-center p-8">
        <div className="max-w-sm rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
          <Monitor className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h2 className="mb-2 text-lg font-semibold text-gray-900">
            Desktop Required
          </h2>
          <p className="text-sm text-gray-600">
            The page editor requires a screen width of at least 1024px. Please
            use a desktop browser.
          </p>
          <button
            onClick={() => navigate("/pages")}
            className="mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Back to Pages
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) return <LoadingSpinner />;

  if (error || !page) {
    return (
      <div className="flex min-h-100 flex-col items-center justify-center gap-4 p-8">
        <h2 className="text-lg font-semibold text-gray-900">Page not found</h2>
        <button
          onClick={() => navigate("/pages")}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Back to Pages
        </button>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col">
      <div className="sticky top-0 z-30 bg-white">
        {/* Editor top bar */}
        <div
          data-onboarding-target="editor-top-bar"
          className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-2"
        >
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/pages")}
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4" />
              Dashboard
            </button>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-gray-900">
                {page.title}
              </h2>
              <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium uppercase text-gray-600">
                {locale}
              </span>
              {isDirty && (
                <span className="text-xs text-yellow-600">Unsaved changes</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <RestartMentorButton
              from="editor_top_bar"
              label="Walkthrough"
              className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
            />
            <button
              data-onboarding-target="editor-top-bar:history"
              onClick={handleHistoryToggle}
              className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
            >
              <History className="h-4 w-4" />
              History
            </button>
            <button
              data-onboarding-target="editor-top-bar:seo"
              onClick={() => {
                setShowSeoPanel(!showSeoPanel);
                setShowVersions(false);
              }}
              className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm cursor-pointer ${
                showSeoPanel
                  ? "border-blue-400 bg-blue-50 text-blue-700"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <Search className="h-4 w-4" />
              SEO
              {seoDirty && (
                <span className="ml-1 h-2 w-2 rounded-full bg-amber-400" />
              )}
            </button>
            <button
              data-onboarding-target="editor-top-bar:preview"
              onClick={handlePreviewOpen}
              className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
            >
              <Eye className="h-4 w-4" />
              Preview
            </button>
            {canPublish && (
              <button
                data-onboarding-target="editor-top-bar:publish"
                onClick={handlePublish}
                disabled={publishMutation.isPending}
                className={`flex items-center cursor-pointer gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${
                  page.published
                    ? "border border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                    : "bg-green-600 text-white hover:bg-green-700"
                }`}
              >
                {publishMutation.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                <Globe className="h-4 w-4" />
                {page.published ? "Unpublish" : "Publish"}
              </button>
            )}
          </div>
        </div>

        {/* Draft recovery banner */}
        {showDraftBanner && (
          <div className="flex items-center justify-between bg-blue-50 px-4 py-2 text-sm">
            <span className="text-blue-800">
              You have an unsaved draft from{" "}
              {draftTime ? formatDate(draftTime) : "earlier"}. Restore it?
            </span>
            <div className="flex gap-2">
              <button
                onClick={handleRestoreDraft}
                className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
              >
                Restore
              </button>
              <button
                onClick={handleDismissDraft}
                className="rounded bg-white px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Main editor area with version history sidebar */}
      <div className="relative flex flex-1 flex-col overflow-hidden">
        <div className="flex flex-1 overflow-hidden">
          {/* Puck Editor */}
          <div
            data-onboarding-target="canvas:iframe"
            className="flex-1 overflow-hidden"
          >
            <LanguageSwitcherRuntimeProvider value={languageSwitcherRuntime}>
              <ContactSectionRuntimeProvider value={contactSectionRuntime}>
                <Puck
                  key={puckKey}
                  config={brandedPuckConfig}
                  iframe={{ enabled: true }}
                  data={
                    (page.puckData as Data) || {
                      content: [],
                      root: { props: {} },
                    }
                  }
                  headerTitle={page.title}
                  headerPath={`/pages/${slug}?locale=${locale}`}
                  onPublish={handleSave}
                  onChange={(data: Data) => {
                    latestDataRef.current = data;
                    setIsDirty(true);
                    // Throttle observed-data updates for the mentor.
                    const ref = observedThrottleRef.current;
                    ref.pending = data;
                    if (ref.timer === null) {
                      ref.timer = window.setTimeout(() => {
                        ref.timer = null;
                        if (ref.pending) setObservedData(ref.pending);
                        ref.pending = null;
                      }, 300);
                    }
                  }}
                  overrides={{
                    drawer: ({ children }) => (
                      <ComponentSearchDrawer
                        componentSearchIndex={componentSearchIndex}
                      >
                        {children}
                      </ComponentSearchDrawer>
                    ),
                    drawerItem: ({ children, name }) => {
                      return (
                        <ComponentSearchDrawerItem
                          name={name}
                          componentSearchIndex={componentSearchIndex}
                        >
                          {children}
                        </ComponentSearchDrawerItem>
                      );
                    },
                    fieldTypes: {
                      text: (props) => (
                        <MarkupFieldChrome
                          field={props.field}
                          readOnly={props.readOnly}
                        >
                          {props.children}
                        </MarkupFieldChrome>
                      ),
                      textarea: (props) => (
                        <MarkupFieldChrome
                          field={props.field}
                          readOnly={props.readOnly}
                        >
                          {props.children}
                        </MarkupFieldChrome>
                      ),
                    },
                    headerActions: () => (
                      <button
                        data-onboarding-target="editor-header:save"
                        onClick={() => {
                          if (latestDataRef.current) {
                            handleSave(latestDataRef.current);
                          }
                        }}
                        disabled={saveMutation.isPending || !isDirty}
                        className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {saveMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        Save
                      </button>
                    ),
                  }}
                />
              </ContactSectionRuntimeProvider>
            </LanguageSwitcherRuntimeProvider>
          </div>

          {/* Version History Sidebar */}
          {showVersions && (
            <div
              data-onboarding-target="panel:versions:root"
              className="w-80 border-l border-gray-200 bg-white shadow-lg z-20 flex flex-col max-h-screen"
            >
              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-[1.33rem]">
                <h3 className="text-md font-semibold text-gray-900">
                  Version History
                </h3>
                <button
                  onClick={() => setShowVersions(false)}
                  className="rounded p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {versionsData?.data && versionsData.data.length > 0 ? (
                  <div className="space-y-3">
                    {versionsData.data.map((version) => (
                      <div
                        key={version.id}
                        className="rounded-md border border-gray-200 p-3"
                      >
                        <div className="mb-1 text-xs text-gray-500">
                          {formatDate(version.savedAt)}
                        </div>
                        <div className="mb-1 text-sm text-gray-700">
                          {version.note || "Auto-save"}
                        </div>
                        <div className="mb-2 text-xs text-gray-400">
                          by {version.savedBy}
                        </div>
                        {canPublish && (
                          <button
                            onClick={() => setRestoreConfirm(version.id)}
                            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                          >
                            <RotateCcw className="h-3 w-3" />
                            Restore
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-sm text-gray-500">
                    No version history
                  </p>
                )}
              </div>
            </div>
          )}

          {/* SEO Sidebar */}
          {showSeoPanel && (
            <PageSeoPanel
              slug={slug!}
              locale={locale}
              page={page}
              puckData={
                (latestDataRef.current as unknown as Record<
                  string,
                  unknown
                > | null) ??
                (page?.puckData as Record<string, unknown> | null) ??
                null
              }
              form={seoForm}
              dirty={seoDirty}
              saving={saveSeoMutation.isPending}
              onChange={(next) => {
                setSeoForm(next);
                setSeoDirty(true);
              }}
              onSave={() => saveSeoMutation.mutate(seoForm)}
              onClose={() => setShowSeoPanel(false)}
            />
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!restoreConfirm}
        title="Restore Version"
        message="Are you sure you want to restore this version? Current changes will be overwritten."
        confirmLabel="Restore"
        onConfirm={() => {
          if (restoreConfirm) {
            restoreMutation.mutate(restoreConfirm);
          }
        }}
        onCancel={() => setRestoreConfirm(null)}
      />
      {mentor.overlay}
    </div>
  );
}

// ─── Page SEO Panel ──────────────────────────────────────────────────────────

interface SeoFormState {
  seoTitle: string;
  seoDescription: string;
  targetKeywords: string;
  internalBrief: string;
  seoOgImage: string;
  seoCanonical: string;
  seoNoindex: boolean;
}

function PageSeoPanel({
  slug,
  locale,
  page,
  puckData,
  form,
  dirty,
  saving,
  onChange,
  onSave,
  onClose,
}: {
  slug: string;
  locale: string;
  page: { title: string; seoTitle?: string };
  puckData: Record<string, unknown> | null;
  form: SeoFormState;
  dirty: boolean;
  saving: boolean;
  onChange: (next: SeoFormState) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  const effectiveTitle = form.seoTitle || page.title || slug;
  const descLen = form.seoDescription.length;
  const titleLen = effectiveTitle.length;

  return (
    <div
      data-onboarding-target="panel:seo:root"
      className="w-96 border-l border-gray-200 bg-white shadow-lg z-20 flex flex-col max-h-screen overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3.5 shrink-0">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900">Page SEO</h3>
          {dirty && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
              Unsaved
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="rounded p-1 text-gray-400 hover:text-gray-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* SERP Preview */}
        <div className="border-b border-gray-100 bg-gray-50 p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-gray-400">
            Search result preview
          </p>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="mb-1 flex items-center gap-1.5 text-xs text-gray-500">
              <div className="h-4 w-4 rounded-sm bg-gray-200" />
              <span>yourdomain.com</span>
              <span className="text-gray-300">›</span>
              <span>{slug}</span>
            </div>
            <p
              className={`text-base font-medium leading-snug text-blue-700 line-clamp-1 ${
                titleLen > 60 ? "text-amber-700" : ""
              }`}
            >
              {effectiveTitle || "Page title"}
            </p>
            <p className="mt-0.5 text-sm text-gray-600 line-clamp-2 leading-relaxed">
              {form.seoDescription || (
                <span className="text-gray-300 italic">
                  No description — add one below
                </span>
              )}
            </p>
          </div>
          {titleLen > 60 && (
            <p className="mt-1.5 flex items-center gap-1 text-xs text-amber-600">
              <AlertCircle className="h-3.5 w-3.5" />
              Title is {titleLen} chars — Google clips at ~60
            </p>
          )}
          {descLen > 160 && (
            <p className="mt-1 flex items-center gap-1 text-xs text-amber-600">
              <AlertCircle className="h-3.5 w-3.5" />
              Description is {descLen} chars — aim for under 160
            </p>
          )}
          {form.seoNoindex && (
            <p className="mt-1.5 flex items-center gap-1 text-xs text-red-600">
              <EyeOff className="h-3.5 w-3.5" />
              This page is set to noindex — it will not appear in search results
            </p>
          )}
        </div>

        {/* OG / Social card preview */}
        {form.seoOgImage && (
          <div className="border-b border-gray-100 bg-gray-50 p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-gray-400">
              Social card preview
            </p>
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              <img
                src={form.seoOgImage}
                alt="OG preview"
                className="aspect-1200/630 w-full object-cover"
                onError={(e) =>
                  ((e.target as HTMLImageElement).style.display = "none")
                }
              />
              <div className="border-t border-gray-100 bg-gray-50 px-3 py-2">
                <p className="text-xs font-medium text-gray-900 line-clamp-1">
                  {effectiveTitle}
                </p>
                <p className="text-[11px] text-gray-500 line-clamp-1">
                  {form.seoDescription}
                </p>
                <p className="mt-0.5 text-[10px] uppercase text-gray-400">
                  yourdomain.com
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Fields */}
        <div className="space-y-5 p-4">
          {/* SEO title */}
          <div>
            <label className="block text-xs font-semibold text-gray-700">
              SEO title
            </label>
            <input
              type="text"
              value={form.seoTitle}
              onChange={(e) => onChange({ ...form, seoTitle: e.target.value })}
              placeholder={page.title || "Leave blank to use page title"}
              className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="mt-1 text-[11px] text-gray-400">
              {titleLen}/60 chars · Overrides the page title in search results
            </p>
          </div>

          {/* Meta description */}
          <div>
            <label className="block text-xs font-semibold text-gray-700">
              Meta description
            </label>
            <textarea
              rows={3}
              value={form.seoDescription}
              onChange={(e) =>
                onChange({ ...form, seoDescription: e.target.value })
              }
              placeholder="Describe this page for search engines…"
              className="mt-1.5 w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p
              className={`mt-1 text-[11px] ${
                descLen > 160
                  ? "text-red-500"
                  : descLen > 140
                    ? "text-amber-500"
                    : "text-gray-400"
              }`}
            >
              {descLen}/160 chars
            </p>
          </div>

          {/* Target Keywords */}
          <div>
            <label className="block text-xs font-semibold text-gray-700">
              Target Keywords
            </label>
            <input
              type="text"
              value={form.targetKeywords}
              onChange={(e) =>
                onChange({ ...form, targetKeywords: e.target.value })
              }
              placeholder="primary keyword, secondary keyword…"
              className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="mt-1 text-[11px] text-gray-400">
              Comma-separated · First keyword is treated as primary
            </p>
          </div>

          {/* Internal Brief */}
          <div>
            <label className="block text-xs font-semibold text-gray-700">
              Internal Brief
            </label>
            <textarea
              rows={4}
              value={form.internalBrief}
              onChange={(e) =>
                onChange({ ...form, internalBrief: e.target.value })
              }
              placeholder="Notes for writers/editors — search intent, angle, key sections, internal links to use…"
              className="mt-1.5 w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              maxLength={5000}
            />
            <p className="mt-1 text-[11px] text-gray-400">
              Not published · Visible only to your team
            </p>
          </div>

          {/* On-page checklist */}
          <SeoChecklist
            slug={slug}
            title={page.title}
            seoTitle={form.seoTitle}
            targetKeywords={form.targetKeywords}
            puckData={puckData}
          />

          {/* OG Image */}
          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
              <Image className="h-3.5 w-3.5" />
              OG / Social image URL
            </label>
            <input
              type="text"
              value={form.seoOgImage}
              onChange={(e) =>
                onChange({ ...form, seoOgImage: e.target.value })
              }
              placeholder="https://example.com/og-image.jpg"
              className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="mt-1 text-[11px] text-gray-400">
              Recommended 1200×630px · Shown when shared on social platforms
            </p>
          </div>

          {/* Canonical */}
          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
              <Link className="h-3.5 w-3.5" />
              Canonical URL override
            </label>
            <input
              type="text"
              value={form.seoCanonical}
              onChange={(e) =>
                onChange({ ...form, seoCanonical: e.target.value })
              }
              placeholder="https://yourdomain.com/page-slug"
              className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="mt-1 text-[11px] text-gray-400">
              Leave blank to use the default canonical derived from the page
              slug
            </p>
          </div>

          {/* noindex toggle */}
          <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-gray-800">
                Exclude from search results
              </p>
              <p className="text-xs text-gray-500">
                Adds a noindex meta tag so search engines skip this page
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                onChange({ ...form, seoNoindex: !form.seoNoindex })
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                form.seoNoindex ? "bg-red-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 translate-x-1 transform rounded-full bg-white shadow transition-transform ${
                  form.seoNoindex ? "translate-x-6" : ""
                }`}
              />
            </button>
          </div>

          {/* Locale note */}
          <p className="text-[11px] text-gray-400">
            These settings apply to the{" "}
            <span className="font-semibold">{locale}</span> version of this
            page.
          </p>
        </div>
      </div>

      {/* Save bar */}
      <div className="shrink-0 border-t border-gray-200 px-4 py-3">
        <button
          onClick={onSave}
          disabled={!dirty || saving}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Save SEO settings
        </button>
      </div>
    </div>
  );
}

// ─── On-page SEO checklist ───────────────────────────────────────────────────

function collectPuckText(puckData: Record<string, unknown> | null): {
  h1Texts: string[];
  bodyText: string;
} {
  if (!puckData) return { h1Texts: [], bodyText: "" };
  const content = (puckData as { content?: unknown }).content;
  if (!Array.isArray(content)) return { h1Texts: [], bodyText: "" };
  const h1Texts: string[] = [];
  const bodyParts: string[] = [];
  for (const block of content as Array<Record<string, unknown>>) {
    const type = typeof block?.type === "string" ? (block.type as string) : "";
    const props =
      (block?.props as Record<string, unknown> | undefined) ?? undefined;
    if (!props) continue;
    const lvl = props.level ?? props.headingLevel ?? props.tag;
    const text =
      typeof props.text === "string"
        ? (props.text as string)
        : typeof props.title === "string"
          ? (props.title as string)
          : typeof props.heading === "string"
            ? (props.heading as string)
            : typeof props.content === "string"
              ? (props.content as string)
              : "";
    if (
      type.toLowerCase().includes("heading") &&
      (lvl === 1 || lvl === "h1" || lvl === "1")
    ) {
      if (text) h1Texts.push(text);
    }
    if (text) bodyParts.push(text);
  }
  return { h1Texts, bodyText: bodyParts.join(" \n") };
}

function SeoChecklist({
  slug,
  title,
  seoTitle,
  targetKeywords,
  puckData,
}: {
  slug: string;
  title: string;
  seoTitle: string;
  targetKeywords: string;
  puckData: Record<string, unknown> | null;
}) {
  const kws = targetKeywords
    .split(",")
    .map((k) => k.trim())
    .filter(Boolean);
  const primary = kws[0] ?? "";
  const lc = (s: string) => s.toLowerCase();
  const { h1Texts, bodyText } = collectPuckText(puckData);

  const hasPrimary = primary.length > 0;
  const inTitle = hasPrimary && lc(seoTitle || title).includes(lc(primary));
  const inH1 = hasPrimary && h1Texts.some((t) => lc(t).includes(lc(primary)));
  const inUrl =
    hasPrimary &&
    lc(slug).includes(
      lc(primary)
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, ""),
    );
  const first100 = bodyText.split(/\s+/).slice(0, 100).join(" ");
  const inFirst100 = hasPrimary && lc(first100).includes(lc(primary));
  const siblings = kws.slice(1);
  const siblingHits = siblings.filter((k) =>
    lc(bodyText).includes(lc(k)),
  ).length;
  const semanticOk = siblings.length === 0 ? false : siblingHits >= 1;

  const items: Array<{ ok: boolean; label: string }> = [
    { ok: inTitle, label: "Primary keyword in title" },
    { ok: inH1, label: "Primary keyword in H1" },
    { ok: inUrl, label: "Primary keyword in URL slug" },
    { ok: inFirst100, label: "Primary keyword in first 100 words" },
    {
      ok: semanticOk,
      label:
        siblings.length === 0
          ? "Add semantic sibling keywords"
          : `Semantic siblings present (${siblingHits}/${siblings.length})`,
    },
  ];

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-700">
          On-page checklist
        </span>
        {hasPrimary ? (
          <span className="text-[11px] text-gray-500">
            primary: <span className="font-medium">{primary}</span>
          </span>
        ) : (
          <span className="text-[11px] text-amber-600">
            Add a target keyword to enable checks
          </span>
        )}
      </div>
      <ul className="space-y-1">
        {items.map((item) => (
          <li
            key={item.label}
            className="flex items-center gap-2 text-[12px] text-gray-700"
          >
            <span
              className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold ${
                item.ok
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-200 text-gray-500"
              }`}
            >
              {item.ok ? "✓" : "·"}
            </span>
            <span className={item.ok ? "" : "text-gray-500"}>{item.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
